import { FormikConfig } from 'formik';
import { History } from 'history';
import * as React from 'react';

import parseISO from 'date-fns/parseISO';
import isFuture from 'date-fns/isFuture';
import { Redirect } from 'react-router-dom';
import { pipe } from 'fp-ts/lib/function';
import { formatInTimeZone } from 'date-fns-tz';
import { ButtonLink, Button } from '../../components/Button';

import { Heading } from '../../components/Heading';
import {
  FullMatchFragment,
  useUpdateMatchMutation,
  useDeleteMatchMutation,
} from '../../generated/graphql';
import { formatErrors } from '../../utils/formatErrors';

import { useAnalytics } from '../../contexts/AnalyticsContext';
import { useAlert } from '../../contexts/AlertContext';
import { dateFormats } from '../../utils/dateFormats';
import { paths } from '../../paths';

import { useConfirm } from '../../contexts/ConfirmContext';
import { MatchForm, MatchFields } from './MatchForm';
import { mapMatchErrors, sanitizeUpdateMatchArgs } from './matchUtils';

interface EditMatchContentProps {
  match: FullMatchFragment;
  history: History;
}

const confirmProps = {
  title: 'Delete Match',
  okButtonText: 'Yes, delete',
  destructive: true,
  children: (
    <>
      <p>Are you sure you want to delete this match?</p>
      <p>This cannot be undone!</p>
    </>
  ),
};

export const EditMatch: React.FC<EditMatchContentProps> = ({
  match,
  history,
}) => {
  const alert = useAlert();
  const analytics = useAnalytics();
  const [updateMatchResponse, updateMatch] = useUpdateMatchMutation();
  const [deleteMatchResponse, deleteMatch] = useDeleteMatchMutation();

  const onSubmit: FormikConfig<MatchFields>['onSubmit'] = (form, actions) => {
    const formEventMeta = { form: 'update_match' };
    return updateMatch({
      input: sanitizeUpdateMatchArgs(form),
      id: match.id,
    }).then((response) => {
      if (response.data?.updateMatch?.validation) {
        analytics.track('Data Submit Failed', formEventMeta);
        pipe(
          response.data.updateMatch.validation,
          formatErrors,
          mapMatchErrors,
          actions.setErrors,
        );
        return;
      }

      if (response.data?.updateMatch?.entity) {
        analytics.track('Data Submit Succeeded', formEventMeta);
        history.push(paths.match.home(match.id));
        alert('Match updated 🎉');
      }
    });
  };

  const confirm = useConfirm();

  const onDeleteMatch = async () => {
    analytics.track('Form Submitted', { form: 'delete_match' });
    const response = await confirm(confirmProps);

    if (response) {
      deleteMatch({ id: match.id }).then((res) => {
        if (res.data?.deleteMatch?.entity) {
          alert('Match deleted 🗑');
          analytics.track('Data Submit Succeeded', { form: 'delete_match' });
          return;
        }
        if (res.error) {
          alert(res.error.message, 'danger');
          analytics.track('Data Submit Failed', {
            form: 'delete_match',
            error: res.error.message,
          });
          return;
        }
      });
    }
  };

  const playedAt = parseISO(match.playedAt);
  const meetAt = match.meetAt ? parseISO(match.meetAt) : undefined;

  if (deleteMatchResponse.data?.deleteMatch?.entity) {
    return <Redirect to={paths.home()} />;
  }

  return (
    <div className="space-y-6">
      <Heading>Edit match</Heading>

      <MatchForm
        initialValues={{
          atHome: match.atHome ? 'yes' : 'no',
          excludedFromStats: match.excludedFromStats ? 'yes' : 'no',
          matchDate: formatInTimeZone(
            playedAt,
            match.timeZone,
            dateFormats.isoDate,
          ),
          meetTime: meetAt
            ? formatInTimeZone(meetAt, match.timeZone, dateFormats.time)
            : '',
          matchTime: formatInTimeZone(
            playedAt,
            match.timeZone,
            dateFormats.time,
          ),
          opponent: match.opponent,
          teamId: match.team.id,
          timeZone: match.timeZone,
        }}
        submitText="Update match"
        onSubmit={onSubmit}
        loading={updateMatchResponse.fetching}
      />
      <ButtonLink to={paths.match.home(match.id)} kind="plain">
        Cancel
      </ButtonLink>
      {isFuture(playedAt) ? (
        <Button kind="destructive" onClick={onDeleteMatch}>
          {deleteMatchResponse.fetching ? 'Loading...' : 'Delete match'}
        </Button>
      ) : null}
    </div>
  );
};
